import { Box, TableBody, TableHead, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { NsnReport } from '@physna/core/domain/nsn-report/nsn-report';
import { StyledHeaderCell, StyledTable, StyledTableCell, StyledTableRow } from './NsnTable';

const SourceSummary = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  marginBottom: '8px',
  cursor: 'default',
}));

interface ManufacturerRow {
  partNumber: string;
  company: string;
  cageCode: string;
  cageStatus?: string;
  source: 'FLIS' | 'NSN-Now';
}

const isActiveCageStatus = (status: string | undefined): boolean => (status ?? '').trim().toLowerCase() === 'active';

export const describeAlternateSources = (flisCount: number, nsnNowCount: number): string =>
  `${flisCount + nsnNowCount} alternate part numbers (FLIS ${flisCount} · NSN-Now ${nsnNowCount})`;

const toRows = ({ manufacturers, nsnNowAlternates = [] }: ManufacturersTableProperties): ManufacturerRow[] => [
  ...manufacturers.map(manufacturer => ({ ...manufacturer, source: 'FLIS' as const })),
  ...nsnNowAlternates.map(alternate => ({ ...alternate, source: 'NSN-Now' as const })),
];

type ManufacturersTableProperties = Pick<NsnReport, 'manufacturers' | 'nsnNowAlternates'>;

export const ManufacturersTable = ({ manufacturers, nsnNowAlternates = [] }: ManufacturersTableProperties) => {
  const showSource = nsnNowAlternates.length > 0;
  const rows = toRows({ manufacturers, nsnNowAlternates });
  return (
    <>
      {showSource && (
        <SourceSummary>{describeAlternateSources(manufacturers.length, nsnNowAlternates.length)}</SourceSummary>
      )}
      <StyledTable size='small'>
        <TableHead>
          <TableRow>
            <StyledHeaderCell>Part Number</StyledHeaderCell>
            <StyledHeaderCell>Company</StyledHeaderCell>
            <StyledHeaderCell>CAGE Code</StyledHeaderCell>
            <StyledHeaderCell>CAGE Status</StyledHeaderCell>
            {showSource && <StyledHeaderCell>Source</StyledHeaderCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <StyledTableRow key={`manufacturer-${row.partNumber}-${index}`}>
              <StyledTableCell>{row.partNumber}</StyledTableCell>
              <StyledTableCell>{row.company}</StyledTableCell>
              <StyledTableCell>{row.cageCode}</StyledTableCell>
              <StyledTableCell
                sx={isActiveCageStatus(row.cageStatus) ? { color: 'success.main', fontWeight: 500 } : undefined}
              >
                {row.cageStatus}
              </StyledTableCell>
              {showSource && <StyledTableCell>{row.source}</StyledTableCell>}
            </StyledTableRow>
          ))}
        </TableBody>
      </StyledTable>
    </>
  );
};
